import * as React from 'react';

import Image from '@/components/elements/Image';
import useHashScroll from '@/hooks/scroll/useHashScroll/HashScroll';
import { dayjst } from '@/lib/dayjst';
import { Blog } from '@/lib/microcms/model';
import { htmlToThemed } from '@/lib/microcms/parseHTML';
import { parseCmsSrcset } from '@/lib/microcms/utils';

import ShareLinks from '../Partials/ShareLinks';
import { BodyWrapper, ContentHeader, PublishedAt, Title } from '../style';
import {
  Author,
  AuthorImage,
  AuthorName,
  Description,
  DisabledLink,
  LinkArea,
  NextIcon,
  PrevIcon,
  ProfileInfo,
  SnsLinks,
  StyledLink,
  TwitterIcon,
} from './style';

interface BlogContentBodyProps {
  content: Blog;
  nextContent: Blog | null;
  prevContent: Blog | null;
}

const BlogContentBody: React.FC<BlogContentBodyProps> = ({
  content,
  nextContent,
  prevContent,
}) => {
  const Link: React.FC<{ type: 'next' | 'prev' }> = ({ children, type }) => {
    if (type === 'next' && nextContent) {
      return <StyledLink to={`/blog/${nextContent.id}`}>{children}</StyledLink>;
    }
    if (type === 'prev' && prevContent) {
      return <StyledLink to={`/blog/${prevContent.id}`}>{children}</StyledLink>;
    }
    return <DisabledLink>{children}</DisabledLink>;
  };
  useHashScroll();
  return (
    <>
      <Image
        srcSet={parseCmsSrcset({ imageUrl: content.featuredImage.url })}
        mediaSet={[
          {
            srcSet: parseCmsSrcset({
              imageUrl: content.featuredImage.url,
              format: 'webp',
            }),
            type: 'image/webp',
          },
        ]}
        aspectRatio={content.featuredImage.height / content.featuredImage.width}
      />
      <ContentHeader>
        <PublishedAt>
          {dayjst(content.publishedAt || content.updatedAt).format('YYYY/M/D')}
        </PublishedAt>
        <Title>{content.title}</Title>
      </ContentHeader>
      <BodyWrapper>{htmlToThemed(content.body)}</BodyWrapper>
      <LinkArea>
        <Link type="prev">
          <PrevIcon />
          <span>前の記事へ</span>
        </Link>
        <Link type="next">
          <span>次の記事へ</span>
          <NextIcon />
        </Link>
      </LinkArea>
      <Author>
        <ProfileInfo>
          <AuthorImage>
            <Image
              srcSet={`${content.author.avatar.url}?w=64&h=64&dpr=1 1x, ${content.author.avatar.url}?w=64&h=64&dpr=2 2x`}
              mediaSet={[
                {
                  srcSet: `${content.author.avatar.url}?w=64&h=64&dpr=1&fm=webp 1x, ${content.author.avatar.url}?w=64&h=64&dpr=2&fm=webp 2x`,
                  type: 'image/webp',
                },
              ]}
              width="64px"
              height="64px"
            />
          </AuthorImage>
          <div>
            <AuthorName to={`/blog?author=${content.author.id}`}>
              {content.author.name}
            </AuthorName>
            <SnsLinks>
              {content.author.twitterId && (
                <a
                  href={`https://twitter.com/${content.author.twitterId}`}
                  target="_blank"
                  rel="noreferrer"
                >
                  <TwitterIcon />
                </a>
              )}
            </SnsLinks>
          </div>
        </ProfileInfo>
        <Description>{content.author.text}</Description>
      </Author>
      <ShareLinks isShowOpinion={false} />
    </>
  );
};

export default BlogContentBody;
